import React, { useEffect, useState } from "react";
import { Search, CheckCircle2, XCircle, MinusCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { StatusBadge } from "../shared/StatusBadge";
import { EmptyState } from "../shared/EmptyState";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { toast } from "sonner";

const STATUS_META = {
  delivered: { label: "Delivered", variant: "success", Icon: CheckCircle2 },
  failed: { label: "Failed", variant: "danger", Icon: XCircle },
  no_device: { label: "No device", variant: "default", Icon: MinusCircle },
};

// Per-customer delivered/failed receipts for a single broadcast. Opened on demand
// (kept out of the main table to stay clean) and searchable by name / phone.
export function PushReceiptsDialog({ correlationId, title, open, onOpenChange }) {
  const [state, setState] = useState({ loading: true, data: null });
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open || !correlationId) return;
    const handle = setTimeout(() => {
      setState((s) => ({ ...s, loading: true }));
      apiClient
        .get("/notifications/receipts", { params: { correlation_id: correlationId, q: q || undefined, limit: 200 } })
        .then(({ data }) => setState({ loading: false, data }))
        .catch((e) => { toast.error(getApiErrorMessage(e, "Could not load receipts.")); setState({ loading: false, data: null }); });
    }, q ? 300 : 0);
    return () => clearTimeout(handle);
  }, [open, correlationId, q]);

  useEffect(() => { if (!open) setQ(""); }, [open]);

  const d = state.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="push-receipts-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">Delivery receipts{title ? ` · ${title}` : ""}</DialogTitle>
        </DialogHeader>

        {d && (
          <div className="flex flex-wrap gap-2" data-testid="push-receipts-summary">
            <StatusBadge label={`${d.delivered} delivered`} variant="success" />
            <StatusBadge label={`${d.failed} failed`} variant="danger" />
            <StatusBadge label={`${d.no_device} no device`} variant="default" />
            <StatusBadge label={`${d.total} total`} variant="default" />
          </div>
        )}

        <div className="relative mt-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or phone…"
            className="pl-9"
            data-testid="push-receipts-search-input"
          />
        </div>

        <div className="max-h-[52vh] overflow-y-auto rounded-md border border-border" data-testid="push-receipts-list">
          {state.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-primary" /></div>
          ) : !d || d.items.length === 0 ? (
            <EmptyState title={q ? "No customers match your search" : "No receipts for this broadcast"} testId="push-receipts-empty" />
          ) : (
            d.items.map((it, i) => {
              const meta = STATUS_META[it.status] || STATUS_META.failed;
              const Icon = meta.Icon;
              return (
                <div
                  key={`${it.customer_id}-${i}`}
                  className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 last:border-b-0"
                  data-testid={`push-receipt-row-${it.customer_id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-text-main">{it.name || `Customer #${it.customer_id}`}</p>
                    {it.phone ? <p className="truncate text-xs text-text-muted">{it.phone}</p> : null}
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Icon size={15} className={it.status === "delivered" ? "text-success" : it.status === "failed" ? "text-danger" : "text-text-muted"} />
                    <StatusBadge label={meta.label} variant={meta.variant} />
                  </span>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
